import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTwoFactorStatus } from "@/hooks/useTwoFactor";
import { useIsAdmin } from "@/hooks/useAdmin";
import { Loader2 } from "lucide-react";
import { isPasswordRecoveryInProgress } from "@/lib/password-recovery";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "student" | "employer" | "admin";
}

const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const { user, role, loading } = useAuth();
  const { data: twoFactorData, isLoading: twoFactorLoading } = useTwoFactorStatus();
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();
  const location = useLocation();

  const isCheckingAdmin = requiredRole === "admin" && adminLoading;

  if (loading || twoFactorLoading || isCheckingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // SECURITY: During password recovery, block access to private pages until password is updated.
  if (location.pathname !== "/reset-password" && isPasswordRecoveryInProgress()) {
    return <Navigate to="/reset-password" replace />;
  }

  if (!user) {
    return <Navigate to="/connexion" replace />;
  }

  // Check if 2FA verification is required
  if (twoFactorData && !twoFactorData.two_factor_verified) {
    return <Navigate to="/verification-2fa" replace />;
  }

  // Admin role check via user_roles table (server-side enforced)
  if (requiredRole === "admin") {
    if (!isAdmin) {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  }

  if (requiredRole && !role) {
    return <Navigate to="/connexion" replace />;
  }

  if (requiredRole && role !== requiredRole) {
    if (role === "student") {
      return <Navigate to="/espace-etudiant" replace />;
    } else if (role === "employer") {
      return <Navigate to="/espace-recruteur" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
